/**
 * Scenic world map pieces used by the Learn path map (docs/CURRICULUM-ROADMAP.md §3):
 *  - WorldMapScene: the world's sky + procedural SVG scene for any size (the fallback).
 *  - useWorldRaster: the painted map `public/worlds/<world>.webp` (`<world>-portrait.webp` in portrait)
 *    when the file exists; PathMap then shows it as a backdrop of the map window (gentle parallax while
 *    scrolling) with a light scrim, places the nodes in the painting's trail band and hides the SVG scene.
 *    Nodes and the trail always stay SVG/HTML on top.
 *  - WorldTrail: the themed trail through the node positions (dirt path, ruler, rail, lanterns, ribbon).
 */
import { memo, useEffect, useMemo, useState, type CSSProperties } from 'react';
import type { PathId } from '@sketchling/core';
import { Figure } from '../art/Figure';
import { WORLD_ART, type WorldTheme } from '../art/worlds';
import type { StyleId } from '../art/tokens';
import { worldMapUrl } from '../lib/responsiveImage';

/* --------------------------- optional raster layer --------------------------- */

const raster = new Map<string, boolean | Promise<boolean>>();

function probe(url: string): Promise<boolean> {
  const hit = raster.get(url);
  if (typeof hit === 'boolean') return Promise.resolve(hit);
  if (hit) return hit;
  const p = new Promise<boolean>((resolve) => {
    if (typeof Image === 'undefined') return resolve(false);
    const img = new Image();
    img.onload = () => resolve(img.naturalWidth > 0);
    img.onerror = () => resolve(false);
    img.src = url;
  }).then((ok) => {
    raster.set(url, ok);
    return ok;
  });
  raster.set(url, p);
  return p;
}

/** URL of the painted map for this world if the file exists (null while checking / missing): the smallest width
 *  variant that covers the window at the (capped) screen density — the map backdrop is about the window wide. */
export function useWorldRaster(world: PathId, portrait: boolean): string | null {
  const url = portrait ? `/worlds/${world}-portrait.webp` : worldMapUrl(world, typeof window === 'undefined' ? 1536 : window.innerWidth);
  const known = raster.get(url);
  const [ok, setOk] = useState<boolean>(known === true);
  useEffect(() => {
    let live = true;
    void probe(url).then((v) => live && setOk(v));
    return () => {
      live = false;
    };
  }, [url]);
  return ok ? url : null;
}

export const isPortrait = () => typeof window !== 'undefined' && window.innerHeight > window.innerWidth;

/* ------------------------------------ sky ------------------------------------ */

function Sky({ theme, width, height, zones, id }: { theme: WorldTheme; width: number; height: number; zones: [number, number][]; id: string }) {
  const stops = theme.zoneSky && zones.length === 4 ? zones.map((z, i) => ({ o: ((z[0] + z[1]) / 2 / width) * 100, c: theme.zoneSky![i]! })) : [{ o: 0, c: theme.sky }];
  return (
    <>
      <defs>
        <linearGradient id={`${id}h`} x1="0" x2="1" y1="0" y2="0">
          {stops.map((s, i) => (
            <stop key={i} offset={`${s.o}%`} stopColor={s.c} />
          ))}
        </linearGradient>
        <linearGradient id={`${id}v`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={theme.skyLow} stopOpacity={0} />
          <stop offset="55%" stopColor={theme.skyLow} stopOpacity={0.85} />
          <stop offset="100%" stopColor={theme.skyLow} stopOpacity={1} />
        </linearGradient>
      </defs>
      <rect x={0} y={0} width={width} height={height} fill={`url(#${id}h)`} />
      <rect x={0} y={0} width={width} height={height * 0.5} fill={`url(#${id}v)`} />
    </>
  );
}

/* ----------------------------------- scene ----------------------------------- */

export interface WorldMapSceneProps {
  world: PathId;
  styleId: StyleId;
  width: number;
  height: number;
  /** x-ranges of the 4 level zones; default = 4 equal zones. */
  zones?: [number, number][];
  className?: string;
  style?: CSSProperties;
}

export const WorldMapScene = memo(function WorldMapScene({ world, styleId, width, height, zones, className = '', style }: WorldMapSceneProps) {
  const art = WORLD_ART[world];
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const z = useMemo<[number, number][]>(() => zones ?? [0, 1, 2, 3].map((i) => [(w * i) / 4, (w * (i + 1)) / 4]), [zones, w]);
  const zKey = z.map((x) => x.map(Math.round).join('-')).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const def = useMemo(() => art.scene({ w, h, zones: z, style: styleId }), [art, w, h, zKey, styleId]);
  const pal = art.palette[styleId];
  const id = `wm-${world}`;
  return (
    <div className={`world-scene w-${world} ${className}`} style={{ position: 'absolute', inset: 0, width: w, height: h, ...style }} data-testid={`world-scene-${world}`} aria-hidden>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ position: 'absolute', inset: 0 }}>
        <Sky theme={art.theme} width={w} height={h} zones={z} id={id} />
      </svg>
      <Figure def={def} styleId={styleId} palette={pal} crop style={{ position: 'absolute', inset: 0, width: w, height: h }} />
    </div>
  );
});

/* ----------------------------------- trail ----------------------------------- */

/** Smooth path through points (horizontal S-curves between nodes). */
export function trailPath(pts: { x: number; y: number }[], from = 0, to = pts.length - 1): string {
  let d = '';
  for (let k = from; k <= to && k < pts.length; k++) {
    const n = pts[k]!;
    if (k === from) d += `M${n.x} ${n.y}`;
    else {
      const p = pts[k - 1]!;
      const mx = (p.x + n.x) / 2;
      d += ` C${mx} ${p.y} ${mx} ${n.y} ${n.x} ${n.y}`;
    }
  }
  return d;
}

export function WorldTrail({ world, points, doneUpTo, width, height, scale, painted }: { world: PathId; points: { x: number; y: number }[]; doneUpTo: number; width: number; height: number; scale: number; painted?: boolean }) {
  const T = WORLD_ART[world].theme.trail;
  const d = trailPath(points);
  const done = doneUpTo > 0 ? trailPath(points, 0, doneUpTo) : '';
  const W = 26 * scale;
  if (painted)
    // Over a painted map: a light dotted guide (the painting already has its own trail).
    return (
      <svg className={`path-trail world-trail trail-painted`} width={width} height={height} aria-hidden>
        <path d={d} fill="none" stroke="rgba(74,50,38,0.35)" strokeWidth={9 * scale} strokeLinecap="round" strokeDasharray={`${0.1} ${16 * scale}`} />
        <path d={d} className="trail-todo" fill="none" stroke="#FFFBF3" strokeWidth={6 * scale} strokeLinecap="round" strokeDasharray={`${0.1} ${16 * scale}`} />
        {done && <path d={done} className="trail-done" fill="none" stroke={WORLD_ART[world].theme.node.done} strokeWidth={7 * scale} strokeLinecap="round" strokeDasharray={`${0.1} ${16 * scale}`} />}
      </svg>
    );
  return (
    <svg className={`path-trail world-trail trail-${T.kind}`} width={width} height={height} aria-hidden>
      <path d={d} fill="none" stroke={T.edge} strokeWidth={W + 5 * scale} strokeLinecap="round" strokeLinejoin="round" opacity={T.kind === 'lantern' ? 0 : 1} />
      {T.kind !== 'lantern' && <path d={d} fill="none" stroke={T.color} strokeWidth={W} strokeLinecap="round" strokeLinejoin="round" />}
      {T.kind === 'dirt' && <path d={d} fill="none" stroke={T.deco} strokeWidth={W * 0.18} strokeDasharray={`${4 * scale} ${22 * scale}`} strokeLinecap="round" opacity={0.8} />}
      {T.kind === 'ruler' && <path d={d} fill="none" stroke={T.deco} strokeWidth={W * 0.45} strokeDasharray={`${1.6 * scale} ${10 * scale}`} opacity={0.7} transform={`translate(0 ${-W * 0.22})`} />}
      {T.kind === 'rail' && (
        <>
          <path d={d} fill="none" stroke={T.edge} strokeWidth={W * 1.1} strokeDasharray={`${5 * scale} ${12 * scale}`} />
          <path d={d} fill="none" stroke={T.deco} strokeWidth={3 * scale} transform={`translate(0 ${-W * 0.32})`} />
          <path d={d} fill="none" stroke={T.deco} strokeWidth={3 * scale} transform={`translate(0 ${W * 0.32})`} />
        </>
      )}
      {T.kind === 'ribbon' && <path d={d} fill="none" stroke={T.deco} strokeWidth={2 * scale} strokeDasharray={`${6 * scale} ${6 * scale}`} opacity={0.7} />}
      {T.kind === 'lantern' && (
        <>
          <path d={d} fill="none" stroke={T.edge} strokeWidth={2.4 * scale} transform={`translate(0 ${-30 * scale})`} />
          <path d={d} fill="none" stroke={T.deco} strokeWidth={11 * scale} strokeDasharray={`${0.1} ${46 * scale}`} strokeLinecap="round" transform={`translate(0 ${-24 * scale})`} className="lantern-glow" />
          <path d={d} fill="none" stroke={T.color} strokeWidth={W * 0.6} strokeLinecap="round" opacity={0.55} />
        </>
      )}
      {done && <path d={done} className="trail-done" fill="none" stroke={WORLD_ART[world].theme.node.done} strokeWidth={5 * scale} strokeLinecap="round" strokeDasharray={`${0.1} ${14 * scale}`} />}
      {!done && <path d={d} className="trail-todo" fill="none" stroke="transparent" />}
    </svg>
  );
}
